import { type FC } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";

import type { SubmittedTimeline } from "@/shared/types/estateEssentials";

interface SubmittedCardProps {
  summary: string;
  note?: string;
  timeline: SubmittedTimeline[];
  deceasedFirstName: string;
  templateValues?: Record<string, string>;
  showViewRequests?: boolean;
}

export const SubmittedCard: FC<SubmittedCardProps> = ({
  summary,
  note,
  timeline,
  deceasedFirstName,
  templateValues = {},
  showViewRequests,
}) => {
  const navigate = useNavigate();
  const resolveTemplates = (text: string) =>
    text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key: string) => {
      if (key === "deceased_first_name") return deceasedFirstName || "your loved one";
      return templateValues[key] ?? "";
    });

  const resolvedSummary = resolveTemplates(summary);
  const resolvedNote = note ? resolveTemplates(note) : undefined;

  return (
    <div className="flex flex-col items-start gap-6 rounded-lg border border-brand bg-brand-subtle p-6">
      <div className="flex items-center gap-2">
        <div className="flex size-5 shrink-0 items-center justify-center rounded-full bg-brand">
          <Check className="size-3 text-white" strokeWidth={3} />
        </div>
        <p className="text-base font-semibold">Request submitted</p>
      </div>
      <p className="text-sm leading-relaxed">{resolvedSummary}</p>
      {resolvedNote && <p className="text-sm leading-relaxed">{resolvedNote}</p>}

      {timeline.length > 0 && (
        <div className="w-full">
          {timeline.map((step, index) => {
            const isLast = index === timeline.length - 1;
            const isFirst = index === 0;
            const dotBg = isLast ? "bg-gray-300" : "bg-green-700";
            const showLine = !isLast;

            return (
              <div key={step.title} className="flex gap-3">
                <div
                  className="relative w-2 shrink-0"
                  style={
                    isFirst && showLine
                      ? {
                          backgroundImage: "linear-gradient(#c5c2c1, #c5c2c1)",
                          backgroundSize: "1px calc(100% - 10px)",
                          backgroundRepeat: "no-repeat",
                          backgroundPosition: "center bottom",
                        }
                      : isLast && !isFirst
                        ? {
                            backgroundImage: "linear-gradient(#c5c2c1, #c5c2c1)",
                            backgroundSize: "1px 10px",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center top",
                          }
                        : !isFirst && !isLast
                          ? {
                              backgroundImage: "linear-gradient(#c5c2c1, #c5c2c1)",
                              backgroundSize: "1px 100%",
                              backgroundRepeat: "no-repeat",
                              backgroundPosition: "center",
                            }
                          : undefined
                  }
                >
                  <div className={`size-2 rounded-full ${dotBg} mt-1.5`} />
                </div>
                <div className={isLast ? "" : "pb-6"}>
                  <p className="text-sm font-semibold">{resolveTemplates(step.title)}</p>
                  <p className="text-sm mt-0.5 text-secondary leading-relaxed">
                    {resolveTemplates(step.description)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showViewRequests && (
        <Button
          variant="outline"
          className="bg-white"
          onClick={() => navigate(POST_LOSS_ROUTES.OVERVIEW)}
        >
          View all submitted requests
        </Button>
      )}
    </div>
  );
};
